import React from 'react';
import { User, Calendar, Clock, FileText, ListTodo, Tag, Link2, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';

const classificationLabels = {
  activity: 'Documentar Actividad',
  account_history: 'Historial de Cuenta',
  relationship_history: 'Historial de Relación',
  tasks: 'Generar Tareas'
};

const typsaCategoryLabels = {
  division: 'División',
  departamento: 'Departamento',
  territorio: 'Territorio',
  grupo: 'Grupo'
};

// Helper to check if AI actually processed the note (has real data)
const hasAIProcessedData = (note) => {
  const isRelationshipHistory = note?.classified_as === 'relationship_history';
  const isAccountHistory = note?.classified_as === 'account_history';
  
  // Check ai_structured_data has actual content
  if (note?.ai_structured_data && Object.keys(note.ai_structured_data).length > 0) {
    if (isRelationshipHistory) {
      return !!note.ai_structured_data.relational_analysis || !!note.ai_structured_data.context_summary;
    }
    if (isAccountHistory) {
      return !!note.ai_structured_data.strategic_analysis || !!note.ai_structured_data.executive_summary;
    }
    return true;
  }
  
  // Fallback checks for older data
  if (isRelationshipHistory) {
    return !!note?.relationship_history_ai_result;
  }
  
  if (isAccountHistory) {
    return !!note?.account_history_ai_result;
  }
  
  // For activity type
  return !!note?.structured_summary?.key_points?.length || 
         !!note?.extracted_tasks_preview?.length ||
         !!note?.activity_ai_result;
};

const VoiceNoteInfoSidebar = ({ note, linkedActivity, linkedContact, linkedCompany, linkedTypsaUnit }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'No disponible';
    return new Date(dateStr).toLocaleString('es-ES', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getTranscriptWordCount = () => {
    if (!note?.transcript_text) return 0;
    return note.transcript_text.split(/\s+/).filter(w => w.length > 0).length;
  };

  const getContactName = () => {
    if (!linkedContact) return 'Contacto vinculado';
    const fullName = `${linkedContact.first_name || ''} ${linkedContact.last_name || ''}`.trim();
    return fullName || linkedContact.email || 'Contacto vinculado';
  };

  // For relationship_history, hide tasks section
  const showTasksSection = note?.classified_as !== 'relationship_history' && note?.extracted_tasks_preview?.length > 0;

  return (
    <Card data-testid="info-sidebar">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Información de la Nota</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Creator */}
        <div className="flex items-start gap-3">
          <User className="w-4 h-4 mt-0.5 text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Creador</p>
            <p className="text-sm font-medium">{note?.creator || note?.created_by_name || 'Desconocido'}</p>
          </div>
        </div>

        {/* Recording Date */}
        <div className="flex items-start gap-3">
          <Calendar className="w-4 h-4 mt-0.5 text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Fecha de grabación</p>
            <p className="text-sm font-medium">{formatDate(note?.date_time || note?.created_at)}</p>
          </div>
        </div>

        {/* Category */}
        <div className="flex items-start gap-3">
          <Tag className="w-4 h-4 mt-0.5 text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Categoría</p>
            <Badge variant="outline" className="mt-1">
              {classificationLabels[note?.classified_as] || 'Sin clasificar'}
            </Badge>
          </div>
        </div>

        {/* Processing Status */}
        <div className="flex items-start gap-3">
          <Clock className="w-4 h-4 mt-0.5 text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Estado de procesamiento</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {note?.transcript_text ? (
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                  Transcrito
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs">Pendiente transcribir</Badge>
              )}
              {hasAIProcessedData(note) ? (
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                  IA procesado
                </Badge>
              ) : note?.transcript_text ? (
                <Badge variant="outline" className="text-xs">Pendiente IA</Badge>
              ) : null}
              {/* Show linked badge for relationship_history */}
              {note?.classified_as === 'relationship_history' && note?.linked_contact_id && (
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                  Vinculado
                </Badge>
              )}
              {/* Show linked badge for account_history - company or TYPSA unit */}
              {note?.classified_as === 'account_history' && (note?.linked_company_id || note?.linked_typsa_org_unit_id) && (
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                  Vinculado
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Transcript Info */}
        {note?.transcript_text && (
          <div className="flex items-start gap-3">
            <FileText className="w-4 h-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Transcripción</p>
              <p className="text-sm font-medium">{getTranscriptWordCount()} palabras</p>
            </div>
          </div>
        )}

        {/* Extracted Tasks - Only show if NOT relationship_history */}
        {showTasksSection && (
          <div className="flex items-start gap-3">
            <ListTodo className="w-4 h-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Tareas identificadas</p>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">{note.extracted_tasks_preview.length} tareas</p>
                {note?.tasks_generated ? (
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                    Creadas
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                    Pendientes
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Linked Activity Status */}
        {note?.linked_activity_id && (
          <div className="pt-3 mt-3 border-t">
            <div className="flex items-start gap-3">
              <Link2 className="w-4 h-4 mt-0.5 text-green-600" />
              <div>
                <p className="text-xs text-muted-foreground">Vinculada a actividad</p>
                {linkedActivity ? (
                  <Link 
                    to="/activities" 
                    className="text-sm font-medium text-green-600 hover:underline"
                    title={`Ver actividad: ${linkedActivity.title}`}
                  >
                    {linkedActivity.title}
                  </Link>
                ) : (
                  <p className="text-sm font-medium text-green-600">Actividad vinculada</p>
                )}
                {linkedActivity?.date && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(linkedActivity.date).toLocaleDateString('es-ES')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Linked Contact Status - For relationship_history */}
        {note?.linked_contact_id && (
          <div className="pt-3 mt-3 border-t">
            <div className="flex items-start gap-3">
              <User className="w-4 h-4 mt-0.5 text-green-600" />
              <div>
                <p className="text-xs text-muted-foreground">Vinculada a contacto</p>
                <Link 
                  to={`/contacts/${note.linked_contact_id}`}
                  className="text-sm font-medium text-green-600 hover:underline"
                  title={`Ver contacto: ${getContactName()}`}
                >
                  {getContactName()}
                </Link>
                {linkedContact?.position && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {linkedContact.position}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Linked Company Status - For account_history */}
        {note?.linked_company_id && (
          <div className="pt-3 mt-3 border-t">
            <div className="flex items-start gap-3">
              <Link2 className="w-4 h-4 mt-0.5 text-green-600" />
              <div>
                <p className="text-xs text-muted-foreground">Vinculada a empresa</p>
                <Link 
                  to={`/companies/${note.linked_company_id}`}
                  className="text-sm font-medium text-green-600 hover:underline"
                  title={`Ver empresa: ${linkedCompany?.name || 'Empresa'}`}
                >
                  {linkedCompany?.name || 'Empresa vinculada'}
                </Link>
                {linkedCompany?.company_type && (
                  <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                    {linkedCompany.company_type}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Linked TYPSA Unit Status - For account_history */}
        {note?.linked_typsa_org_unit_id && (
          <div className="pt-3 mt-3 border-t">
            <div className="flex items-start gap-3">
              <Building2 className="w-4 h-4 mt-0.5 text-blue-600" />
              <div>
                <p className="text-xs text-muted-foreground">Vinculada a unidad TYPSA</p>
                <Link 
                  to={`/typsa/${note.linked_typsa_org_unit_id}`}
                  className="text-sm font-medium text-blue-600 hover:underline"
                  title={`Ver unidad: ${linkedTypsaUnit?.name || linkedTypsaUnit?.value || 'Unidad TYPSA'}`}
                >
                  {linkedTypsaUnit?.name || linkedTypsaUnit?.value || 'Unidad TYPSA vinculada'}
                </Link>
                {linkedTypsaUnit?.category && (
                  <Badge variant="outline" className="mt-1 text-xs border-blue-300 text-blue-700">
                    {typsaCategoryLabels[linkedTypsaUnit.category] || linkedTypsaUnit.category}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VoiceNoteInfoSidebar;
